import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowLeft, Download, RotateCw, BarChart3 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as XLSX from "xlsx";

import {
  CHART,
  ChartTooltip,
  EmptyState,
  GlassCard,
  PageHeader,
  PageShell,
  SERIES,
  STATUS_COLORS,
  StatRow,
  StatTile,
  T,
  TileSkeleton,
  formatCompact,
  formatCurrency,
  formatDuration,
  ghostButtonSx,
} from "./shared";
import { fetchReport, fetchSettings, errorMessage } from "./api";

/**
 * Report catalogue. `endpoint` is appended to /complaints/reports, and `tables`
 * describes how to render each slice of the response, so adding a report is a
 * config change rather than a new component.
 *
 * Every report ships a table. That is deliberate: several palette slots sit
 * below 3:1 contrast on white, and a readable table is the required relief.
 */
const REPORTS = [
  {
    group: "Operational",
    key: "dashboard",
    endpoint: "dashboard",
    label: "Overview",
    summaryKey: "kpis",
    tables: [
      { key: "byStatus", title: "By Status", columns: [["status", "Status"], ["count", "Complaints"]] },
      { key: "openByPriority", title: "Open by Priority", columns: [["priority", "Priority"], ["count", "Complaints"]] },
      { key: "topCategories", title: "Top Categories", columns: [["category", "Category"], ["count", "Complaints"]] },
    ],
  },
  {
    group: "Operational",
    key: "ticket-volume",
    endpoint: "ticket-volume",
    label: "Ticket Volume",
    supportsGroupBy: true,
    chart: {
      type: "line",
      data: "periods",
      x: "period",
      title: "Volume over time",
      series: [["total", "Total"], ["resolved", "Resolved"], ["open", "Open"]],
    },
    tables: [
      { key: "categories", title: "By Category", columns: [["category", "Category"], ["total", "Total"], ["resolved", "Resolved"], ["avgResolutionMins", "Avg Resolution", "duration"]] },
      { key: "subCategories", title: "By Sub-category", columns: [["category", "Category"], ["subCategory", "Sub-category"], ["total", "Total"]] },
      { key: "sources", title: "By Source", columns: [["source", "Source"], ["total", "Total"]] },
      { key: "priorities", title: "By Priority", columns: [["priority", "Priority"], ["total", "Total"]] },
    ],
  },
  {
    group: "Operational",
    key: "sla-compliance",
    endpoint: "sla-compliance",
    label: "SLA Compliance",
    summaryKey: "summary",
    tables: [
      { key: "byCategory", title: "By Category", columns: [["category", "Category"], ["total", "Total"], ["resolved", "Resolved"], ["breached", "Breached"], ["compliancePct", "Compliance", "pct"], ["avgResolutionMins", "Avg Resolution", "duration"]] },
      { key: "byPriority", title: "By Priority", columns: [["priority", "Priority"], ["total", "Total"], ["breached", "Breached"], ["compliancePct", "Compliance", "pct"]] },
    ],
  },
  {
    group: "Operational",
    key: "agent-productivity",
    endpoint: "agent-productivity",
    label: "Agent Productivity",
    tables: [
      {
        key: null,
        title: "Per Agent",
        columns: [
          ["agentName", "Agent"], ["handled", "Handled"], ["resolved", "Resolved"], ["open", "Open"],
          ["resolutionRatePct", "Resolution Rate", "pct"], ["avgFirstResponseMins", "Avg FRT", "duration"],
          ["avgResolutionMins", "Avg Resolution", "duration"], ["slaBreaches", "SLA Breaches"],
          ["reopened", "Reopened"], ["avgCsat", "Avg CSAT"], ["refundAmount", "Payout", "currency"],
        ],
      },
    ],
  },
  {
    group: "Operational",
    key: "first-response-time",
    endpoint: "first-response-time",
    label: "First Response Time",
    summaryKey: "summary",
    supportsGroupBy: true,
    chart: {
      type: "line",
      data: "trend",
      x: "period",
      title: "Average first response time",
      valueFormat: "duration",
      series: [["avgFrtMins", "Avg first response"]],
    },
    tables: [
      { key: "byCategory", title: "By Category", columns: [["category", "Category"], ["total", "Total"], ["avgFrtMins", "Avg FRT", "duration"], ["breaches", "Breaches"]] },
      { key: "byAgent", title: "By Agent", columns: [["agentName", "Agent"], ["total", "Total"], ["avgFrtMins", "Avg FRT", "duration"], ["breaches", "Breaches"]] },
    ],
  },
  {
    group: "Operational",
    key: "escalations",
    endpoint: "escalations",
    label: "Escalations",
    summaryKey: "summary",
    tables: [
      { key: "byQueue", title: "By Destination", columns: [["queueLabel", "Forwarded To"], ["count", "Total"], ["manual", "Manual"], ["slaTriggered", "SLA-triggered"]] },
      { key: "byCategory", title: "By Category", columns: [["category", "Category"], ["escalated", "Escalated"], ["avgResolutionMins", "Avg Resolution", "duration"]] },
      { key: "byReason", title: "Reasons", columns: [["reason", "Reason"], ["count", "Count"]] },
    ],
  },
  {
    group: "Operational",
    key: "reopen-rate",
    endpoint: "reopen-rate",
    label: "Reopen Rate",
    summaryKey: "summary",
    tables: [
      { key: "byCategory", title: "By Category", columns: [["category", "Category"], ["total", "Total"], ["reopened", "Reopened"], ["reopenRatePct", "Reopen Rate", "pct"]] },
      { key: "byAgent", title: "By Agent", columns: [["agentName", "Agent"], ["total", "Total"], ["reopened", "Reopened"], ["reopenRatePct", "Reopen Rate", "pct"]] },
    ],
  },
  {
    group: "Customer Experience",
    key: "csat",
    endpoint: "csat",
    label: "CSAT / NPS",
    summaryKey: "summary",
    supportsGroupBy: true,
    chart: {
      type: "bar",
      data: "ratingDistribution",
      x: "rating",
      title: "Rating distribution",
      xLabel: "Stars",
      directLabels: true,
      series: [["count", "Responses"]],
    },
    tables: [
      { key: "byCategory", title: "By Category", columns: [["category", "Category"], ["responses", "Responses"], ["avgCsat", "Avg CSAT"], ["avgNps", "Avg NPS"]] },
      { key: "byAgent", title: "By Agent", columns: [["agentName", "Agent"], ["responses", "Responses"], ["avgCsat", "Avg CSAT"]] },
    ],
  },
  {
    group: "Customer Experience",
    key: "trend",
    endpoint: "trend",
    label: "Complaint Trend",
    tables: [
      { key: "topDrivers", title: "Top Complaint Drivers", columns: [["category", "Category"], ["subCategory", "Sub-category"], ["count", "Now"], ["previousCount", "Previous"], ["changePct", "Change", "pct"], ["avgCsat", "Avg CSAT"]] },
      { key: "rising", title: "Rising Fastest", columns: [["category", "Category"], ["subCategory", "Sub-category"], ["count", "Now"], ["changePct", "Change", "pct"]] },
      { key: "newIssues", title: "New This Period", columns: [["category", "Category"], ["subCategory", "Sub-category"], ["count", "Count"]] },
      { key: "repeatComplainants", title: "Repeat Complainants", columns: [["name", "Customer"], ["mobile", "Mobile"], ["complaints", "Complaints"]] },
    ],
  },
  {
    group: "Customer Experience",
    key: "root-cause",
    endpoint: "root-cause",
    label: "Root Cause Analysis",
    tables: [
      {
        key: "causes", title: "Ranked by Impact",
        columns: [["category", "Category"], ["subCategory", "Sub-category"], ["impactScore", "Impact"], ["volume", "Volume"], ["totalPayout", "Payout", "currency"], ["escalated", "Escalated"], ["reopened", "Reopened"], ["slaBreaches", "SLA Breaches"], ["avgCsat", "Avg CSAT"]],
      },
      { key: "topRestaurants", title: "Restaurants Driving Complaints", columns: [["restaurantName", "Restaurant"], ["complaints", "Complaints"], ["payout", "Payout", "currency"]] },
      { key: "topDeliveryPartners", title: "Delivery Partners Driving Complaints", columns: [["deliveryPartnerName", "Partner"], ["deliveryPartnerId", "Partner ID"], ["complaints", "Complaints"]] },
    ],
  },
  {
    group: "Financial",
    key: "refunds",
    endpoint: "refunds",
    label: "Refund / Compensation",
    summaryKey: "summary",
    supportsGroupBy: true,
    chart: {
      type: "bar",
      data: "trend",
      x: "period",
      title: "Payout over time",
      valueFormat: "currency",
      series: [["totalAmount", "Payout"]],
    },
    tables: [
      { key: "byCategory", title: "By Category", columns: [["category", "Category"], ["tickets", "Tickets"], ["totalAmount", "Total", "currency"], ["avgAmount", "Average", "currency"]] },
      { key: "byResolutionType", title: "By Resolution Type", columns: [["resolutionType", "Type"], ["tickets", "Tickets"], ["totalAmount", "Total", "currency"]] },
      { key: "byRefundStatus", title: "By Refund Status", columns: [["refundStatus", "Status"], ["tickets", "Tickets"], ["totalAmount", "Total", "currency"]] },
    ],
  },
  {
    group: "Financial",
    key: "cost-of-resolution",
    endpoint: "cost-of-resolution",
    label: "Cost of Resolution",
    summaryKey: "summary",
    supportsHourlyRate: true,
    tables: [
      {
        key: "byCategory", title: "By Category",
        columns: [["category", "Category"], ["tickets", "Tickets"], ["payout", "Payout", "currency"], ["handlingHours", "Handling Hours"], ["labourCost", "Labour Cost", "currency"], ["totalCost", "Total Cost", "currency"], ["costPerTicket", "Cost / Ticket", "currency"]],
      },
    ],
  },
  {
    group: "Department-wise",
    key: "by-restaurant",
    endpoint: "by/restaurant",
    label: "Restaurant-wise",
    tables: [{ key: null, title: "Per Restaurant", columns: [["restaurantName", "Restaurant"], ["complaints", "Complaints"], ["resolved", "Resolved"], ["openTickets", "Open"], ["slaBreaches", "SLA Breaches"], ["payout", "Payout", "currency"], ["avgCsat", "Avg CSAT"]] }],
  },
  {
    group: "Department-wise",
    key: "by-delivery-partner",
    endpoint: "by/delivery-partner",
    label: "Delivery Partner-wise",
    tables: [{ key: null, title: "Per Delivery Partner", columns: [["deliveryPartnerName", "Partner"], ["entityId", "Partner ID"], ["complaints", "Complaints"], ["resolved", "Resolved"], ["slaBreaches", "SLA Breaches"], ["avgCsat", "Avg CSAT"]] }],
  },
  {
    group: "Department-wise",
    key: "by-city",
    endpoint: "by/city",
    label: "City-wise",
    tables: [{ key: null, title: "Per City", columns: [["city", "City"], ["complaints", "Complaints"], ["resolved", "Resolved"], ["slaBreaches", "SLA Breaches"], ["payout", "Payout", "currency"], ["avgCsat", "Avg CSAT"]] }],
  },
  {
    group: "Department-wise",
    key: "by-region",
    endpoint: "by/region",
    label: "Region-wise",
    tables: [{ key: null, title: "Per Region", columns: [["region", "Region"], ["complaints", "Complaints"], ["resolved", "Resolved"], ["slaBreaches", "SLA Breaches"], ["payout", "Payout", "currency"]] }],
  },
  {
    group: "Department-wise",
    key: "by-department",
    endpoint: "by/department",
    label: "Department-wise",
    tables: [{ key: null, title: "Per Department", columns: [["department", "Department"], ["complaints", "Complaints"], ["resolved", "Resolved"], ["openTickets", "Open"], ["slaBreaches", "SLA Breaches"], ["avgResolutionMins", "Avg Resolution", "duration"]] }],
  },
];

/** Formats a cell according to the column's declared type. */
const renderCell = (value, format) => {
  if (value === null || value === undefined || value === "") return "—";
  if (format === "currency") return formatCurrency(value);
  if (format === "duration") return formatDuration(value);
  if (format === "pct") return `${value}%`;
  return String(value);
};

/** Turns a KPI key like `slaCompliancePct` into "SLA Compliance". */
const humanise = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/ Pct$/, "")
    .replace(/ Mins$/, "")
    .replace(/Sla/g, "SLA")
    .replace(/Csat/g, "CSAT")
    .replace(/Nps/g, "NPS")
    .replace(/Frt/g, "FRT");

/** Picks the tone for a KPI tile from what the metric means. */
const toneFor = (key, value) => {
  const lower = key.toLowerCase();
  if (lower.includes("breach") || lower.includes("overdue") || lower.includes("detractor")) {
    return value > 0 ? "critical" : "good";
  }
  if (lower.includes("compliance") || lower.includes("rate") || lower.includes("promoter")) {
    if (lower.includes("reopen") || lower.includes("escalation")) return value > 15 ? "warning" : "good";
    return value >= 90 ? "good" : value >= 75 ? "warning" : "critical";
  }
  if (lower.includes("pending")) return value > 0 ? "warning" : "neutral";
  return "neutral";
};

const formatKpi = (key, value) => {
  if (typeof value !== "number") return value;
  if (key.endsWith("Pct")) return `${value}%`;
  if (/amount|cost|payout/i.test(key)) return formatCurrency(value);
  if (key.endsWith("Mins")) return formatDuration(value);
  return formatCompact(value);
};

const SummaryTiles = ({ summary }) => (
  <StatRow columns={4}>
    {Object.entries(summary)
      .filter(([, value]) => typeof value === "number" || typeof value === "string")
      .map(([key, value]) => (
        <StatTile
          key={key}
          label={humanise(key)}
          value={formatKpi(key, value)}
          tone={toneFor(key, value)}
        />
      ))}
  </StatRow>
);

const ReportTable = ({ title, columns, rows }) => (
  <GlassCard sx={{ mb: 2, overflow: "hidden" }}>
    <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${T.border}` }}>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: T.ink }}>{title}</Typography>
    </Box>

    {rows?.length ? (
      <TableContainer sx={{ maxHeight: 460 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map(([key, label]) => (
                <TableCell
                  key={key}
                  sx={{
                    fontWeight: 700,
                    bgcolor: T.surfaceMuted,
                    fontSize: "0.7rem",
                    color: T.inkSecondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    borderBottom: `1px solid ${T.border}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index} hover>
                {columns.map(([key, , format]) => (
                  <TableCell
                    key={key}
                    sx={{
                      fontSize: "0.8rem",
                      color: T.ink,
                      borderBottom: `1px solid ${T.border}`,
                      // Figures align down the column, so equal-width digits help here.
                      fontVariantNumeric: format ? "tabular-nums" : "normal",
                    }}
                  >
                    {renderCell(row[key], format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <EmptyState dense icon={BarChart3} title="No data for this period" />
    )}
  </GlassCard>
);

const ComplaintReports = () => {
  const navigate = useNavigate();

  const [selected, setSelected] = useState(REPORTS[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [range, setRange] = useState({ from: "", to: "", groupBy: "day", hourlyRate: "" });

  // Reports render plenty of money. This page does not load form metadata, so it
  // fetches settings directly to learn the workspace currency before formatting.
  useEffect(() => {
    fetchSettings()
      .then((s) => setRange((p) => ({ ...p, hourlyRate: p.hourlyRate || s?.costPerAgentHour || 250 })))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      if (selected.supportsGroupBy) params.groupBy = range.groupBy;
      if (selected.supportsHourlyRate) params.hourlyRate = range.hourlyRate;

      setData(await fetchReport(selected.endpoint, params));
    } catch (err) {
      setError(errorMessage(err, "Failed to load report"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selected, range]);

  useEffect(() => {
    load();
  }, [load]);

  /** Rows for a table definition, handling reports that return a bare array. */
  const rowsFor = useCallback(
    (table) => {
      if (!data) return [];
      return table.key === null ? (Array.isArray(data) ? data : []) : data[table.key] || [];
    },
    [data]
  );

  const chartData = useMemo(() => {
    if (!selected.chart || !data) return [];
    return data[selected.chart.data] || [];
  }, [selected, data]);

  const tooltipFormatter = useMemo(() => {
    if (selected.chart?.valueFormat === "currency") return formatCurrency;
    if (selected.chart?.valueFormat === "duration") return formatDuration;
    return undefined;
  }, [selected]);

  const handleExport = () => {
    if (!data) return;

    const workbook = XLSX.utils.book_new();

    if (selected.summaryKey && data[selected.summaryKey]) {
      const summary = Object.entries(data[selected.summaryKey]).map(([key, value]) => ({
        Metric: humanise(key),
        Value: value,
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summary), "Summary");
    }

    selected.tables.forEach((table) => {
      const rows = rowsFor(table);
      if (!rows.length) return;

      const sheet = rows.map((row) => {
        const out = {};
        table.columns.forEach(([key, label]) => {
          out[label] = row[key] ?? "";
        });
        return out;
      });

      // Excel sheet names cap at 31 characters.
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet), table.title.slice(0, 31));
    });

    if (!workbook.SheetNames.length) return;
    XLSX.writeFile(workbook, `${selected.key}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const grouped = useMemo(() => {
    const map = new Map();
    REPORTS.forEach((report) => {
      if (!map.has(report.group)) map.set(report.group, []);
      map.get(report.group).push(report);
    });
    return Array.from(map.entries());
  }, []);

  const summary = selected.summaryKey ? data?.[selected.summaryKey] : null;
  const statusRows = selected.key === "dashboard" ? data?.byStatus || [] : [];

  return (
    <PageShell>
            <Breadcrumbs sx={{ mb: 1.5 }}>
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate("/complaints")}
                sx={{ fontSize: "0.78rem", color: T.inkSecondary }}
              >
                Complaints
              </Link>
              <Typography sx={{ fontSize: "0.78rem", color: T.ink, fontWeight: 600 }}>Reports</Typography>
            </Breadcrumbs>

            <PageHeader
              icon={BarChart3}
              title="Complaint Reports"
              subtitle="Operational, customer experience, financial and department-wise reporting."
              actions={
                <>
                  <IconButton
                    size="small"
                    onClick={() => navigate("/complaints")}
                    sx={{ border: `1px solid ${T.border}`, borderRadius: T.radiusControl }}
                  >
                    <ArrowLeft size={16} color={T.inkSecondary} />
                  </IconButton>
                  <Tooltip title="Refresh">
                    <IconButton
                      size="small"
                      onClick={load}
                      sx={{ border: `1px solid ${T.border}`, borderRadius: T.radiusControl }}
                    >
                      <RotateCw size={16} color={T.inkSecondary} />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download size={16} />}
                    onClick={handleExport}
                    disabled={!data}
                    sx={ghostButtonSx}
                  >
                    Export Excel
                  </Button>
                </>
              }
            />

            {/* ---- Report picker + range ---- */}
            <GlassCard sx={{ p: 2.5, mb: 2 }}>
              {grouped.map(([group, reports]) => (
                <Box key={group} sx={{ mb: 1.75 }}>
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: T.inkMuted,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      mb: 0.85,
                    }}
                  >
                    {group}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {reports.map((report) => {
                      const active = selected.key === report.key;
                      return (
                        <Chip
                          key={report.key}
                          label={report.label}
                          size="small"
                          onClick={() => setSelected(report)}
                          sx={{
                            borderRadius: "999px",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            height: 28,
                            px: 0.5,
                            bgcolor: active ? T.brand : T.surfaceMuted,
                            color: active ? "#fff" : T.inkSecondary,
                            border: `1px solid ${active ? T.brand : T.border}`,
                            "&:hover": {
                              bgcolor: active ? "#4338CA" : T.brandSoft,
                              color: active ? "#fff" : T.brand,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ))}

              {/* Filters sit in one row above the charts. */}
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    fullWidth size="small" label="From" type="date" InputLabelProps={{ shrink: true }}
                    value={range.from} onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    fullWidth size="small" label="To" type="date" InputLabelProps={{ shrink: true }}
                    value={range.to} onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))}
                  />
                </Grid>
                {selected.supportsGroupBy && (
                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      select fullWidth size="small" label="Group by"
                      value={range.groupBy}
                      onChange={(e) => setRange((p) => ({ ...p, groupBy: e.target.value }))}
                    >
                      <MenuItem value="day">Daily</MenuItem>
                      <MenuItem value="week">Weekly</MenuItem>
                      <MenuItem value="month">Monthly</MenuItem>
                    </TextField>
                  </Grid>
                )}
                {selected.supportsHourlyRate && (
                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      fullWidth size="small" type="number" label="Agent hourly rate"
                      value={range.hourlyRate}
                      onChange={(e) => setRange((p) => ({ ...p, hourlyRate: e.target.value }))}
                    />
                  </Grid>
                )}
              </Grid>
            </GlassCard>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: T.radiusControl }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <>
                <TileSkeleton count={4} />
                <Skeleton variant="rounded" height={320} sx={{ borderRadius: T.radiusCard, mb: 2 }} />
                <Skeleton variant="rounded" height={280} sx={{ borderRadius: T.radiusCard }} />
              </>
            ) : (
              <>
                {summary && <SummaryTiles summary={summary} />}

                {/* ---- Trend chart ---- */}
                {selected.chart && chartData.length > 0 && (
                  <GlassCard sx={{ p: 2.5, mb: 2 }}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: T.ink, mb: 2 }}>
                      {selected.chart.title}
                    </Typography>
                    {/* Height includes the x-axis band so labels are never clipped. */}
                    <ResponsiveContainer width="100%" height={300}>
                      {selected.chart.type === "bar" ? (
                        <BarChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 8 }}>
                          <CartesianGrid stroke={CHART.grid} vertical={false} />
                          <XAxis dataKey={selected.chart.x} tick={CHART.tick} {...CHART.axisProps} />
                          <YAxis tick={CHART.tick} {...CHART.axisProps} width={48} />
                          <ReTooltip
                            cursor={{ fill: "rgba(99,102,241,0.06)" }}
                            content={<ChartTooltip formatter={tooltipFormatter} />}
                          />
                          {selected.chart.series.length > 1 && (
                            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" iconSize={8} />
                          )}
                          {selected.chart.series.map(([key, label], index) => (
                            <Bar
                              key={key}
                              dataKey={key}
                              name={label}
                              fill={SERIES[index % SERIES.length]}
                              radius={[4, 4, 0, 0]}
                              maxBarSize={38}
                            >
                              {/* A single series can carry its values directly. */}
                              {selected.chart.directLabels && (
                                <LabelList
                                  dataKey={key}
                                  position="top"
                                  style={{ fontSize: 11, fill: T.inkSecondary }}
                                />
                              )}
                            </Bar>
                          ))}
                        </BarChart>
                      ) : (
                        <LineChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 8 }}>
                          <CartesianGrid stroke={CHART.grid} vertical={false} />
                          <XAxis dataKey={selected.chart.x} tick={CHART.tick} {...CHART.axisProps} />
                          <YAxis tick={CHART.tick} {...CHART.axisProps} width={48} />
                          <ReTooltip content={<ChartTooltip formatter={tooltipFormatter} />} />
                          {selected.chart.series.length > 1 && (
                            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" iconSize={8} />
                          )}
                          {selected.chart.series.map(([key, label], index) => (
                            <Line
                              key={key}
                              type="monotone"
                              dataKey={key}
                              name={label}
                              stroke={SERIES[index % SERIES.length]}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }}
                            />
                          ))}
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  </GlassCard>
                )}

                {/* Status split reads as a ranked bar, not a pie — six states are
                    too many to compare by angle, and each keeps its status colour. */}
                {selected.key === "dashboard" && statusRows.length > 0 && (
                  <GlassCard sx={{ p: 2.5, mb: 2 }}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: T.ink, mb: 2 }}>
                      Complaints by status
                    </Typography>
                    <ResponsiveContainer width="100%" height={Math.max(180, statusRows.length * 42)}>
                      <BarChart
                        data={[...statusRows].sort((a, b) => b.count - a.count)}
                        layout="vertical"
                        margin={{ top: 4, right: 44, left: 12, bottom: 4 }}
                      >
                        <CartesianGrid stroke={CHART.grid} horizontal={false} />
                        <XAxis type="number" tick={CHART.tick} {...CHART.axisProps} />
                        <YAxis
                          type="category"
                          dataKey="status"
                          tick={CHART.tick}
                          {...CHART.axisProps}
                          width={92}
                        />
                        <ReTooltip cursor={{ fill: "rgba(99,102,241,0.06)" }} content={<ChartTooltip />} />
                        <Bar dataKey="count" name="Complaints" radius={[0, 4, 4, 0]} maxBarSize={22}>
                          {statusRows.map((row) => (
                            <Cell
                              key={row.status}
                              fill={(STATUS_COLORS[row.status] || STATUS_COLORS.Closed).dot}
                            />
                          ))}
                          <LabelList
                            dataKey="count"
                            position="right"
                            style={{ fontSize: 11, fill: T.inkSecondary, fontWeight: 600 }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </GlassCard>
                )}

                {selected.tables.map((table) => (
                  <ReportTable
                    key={table.title}
                    title={table.title}
                    columns={table.columns}
                    rows={rowsFor(table)}
                  />
                ))}
              </>
            )}
    </PageShell>
  );
};

export default ComplaintReports;
